import React, { useState, useMemo, useEffect } from "react";
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  TableHead,
  Card,
  CardContent,
  TablePagination,
  Menu,
  IconButton,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import {
  ALL_TERMINALS_VALUE,
  flattenReportTransactions,
} from "./TransactionSearchReport.utils";
import { formatUtcToLocal, getLocalTzLabel } from "../../../services/dateUtils";

const tzLabel = getLocalTzLabel();

function TransactionSearchReportTransactionsTable({
  data,
  terminalFilter,
  onTerminalFilterChange,
}) {
  const formatDateTime = (value) =>
    formatUtcToLocal(value, { second: "2-digit" }) || "N/A";

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [statusFilter, setStatusFilter] = useState("E");
  const [anchorEl, setAnchorEl] = useState(null);

  const terminalOptions = useMemo(() => {
    const ids = (data?.terminals || [])
      .map((t) => t.terminalID)
      .filter((id) => id != null && String(id).trim() !== "");
    return [...new Set(ids.map(String))];
  }, [data]);

  const allTransactions = useMemo(
    () => flattenReportTransactions(data),
    [data],
  );

  useEffect(() => {
    if (terminalFilter === ALL_TERMINALS_VALUE) return;
    if (!terminalOptions.includes(terminalFilter)) {
      onTerminalFilterChange(ALL_TERMINALS_VALUE);
    }
  }, [terminalFilter, terminalOptions, onTerminalFilterChange]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleFilterClose = (filter) => {
    setStatusFilter(filter);
    setAnchorEl(null);
    setPage(0);
  };

  const handleTerminalChange = (event) => {
    onTerminalFilterChange(event.target.value);
    setPage(0);
  };

  if (!data) {
    return <Typography>No data available</Typography>;
  }

  const { acquirer, terminals, fiat } = data;

  const terminalScoped =
    terminalFilter === ALL_TERMINALS_VALUE
      ? allTransactions
      : allTransactions.filter(
          (txn) => String(txn.terminalID) === terminalFilter,
        );

  const filteredTransactions = terminalScoped.filter((txn) => {
    if (statusFilter === "All") return true;
    return txn.blokko?.transactionStatus === statusFilter;
  });

  const sortedTransactions = [...filteredTransactions].sort((a, b) => {
    const timeA = a.rate?.requestTime || "";
    const timeB = b.rate?.requestTime || "";
    return timeB.localeCompare(timeA);
  });

  return (
    <Card sx={{ mt: 3, p: 2 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Merchant & Terminal Details
        </Typography>
        <TableContainer component={Paper} sx={{ mb: 3 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold" }}>
                  Merchant Name
                </TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>
                  Merchant Store
                </TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Currency</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>{acquirer?.merchantName || "N/A"}</TableCell>
                <TableCell>{acquirer?.merchantStore || "N/A"}</TableCell>
                <TableCell>{fiat?.currencyCode || "N/A"}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 2,
            mb: 2,
          }}
        >
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Transaction Details
          </Typography>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel id="transaction-search-report-terminal-filter">
              Terminal
            </InputLabel>
            <Select
              labelId="transaction-search-report-terminal-filter"
              value={
                terminalOptions.length === 0
                  ? ALL_TERMINALS_VALUE
                  : terminalFilter === ALL_TERMINALS_VALUE ||
                      terminalOptions.includes(terminalFilter)
                    ? terminalFilter
                    : ALL_TERMINALS_VALUE
              }
              label="Terminal"
              onChange={handleTerminalChange}
              disabled={!terminals?.length}
            >
              <MenuItem value={ALL_TERMINALS_VALUE}>All terminals</MenuItem>
              {terminalOptions.map((id) => (
                <MenuItem key={id} value={id}>
                  {id}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow
                sx={{
                  "& .MuiTableCell-root": {
                    fontWeight: 700,
                    backgroundColor: "primary.main",
                    color: "primary.contrastText",
                    fontSize: "0.875rem",
                  },
                }}
              >
                <TableCell>Terminal ID</TableCell>
                <TableCell>Transaction</TableCell>
                <TableCell>
                  {tzLabel ? `QR Datetime (${tzLabel})` : "QR Datetime"}
                </TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Currency</TableCell>
                <TableCell>Exchange</TableCell>
                <TableCell>
                  {tzLabel ? `Update Datetime (${tzLabel})` : "Update Datetime"}
                </TableCell>
                <TableCell sx={{ position: "relative" }}>
                  <Box
                    component="span"
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    Status
                    <IconButton
                      onClick={handleFilterClick}
                      size="small"
                      sx={{
                        padding: 0.5,
                        color: "inherit",
                        "&:hover": {
                          backgroundColor: "rgba(255, 255, 255, 0.16)",
                        },
                      }}
                    >
                      <FilterListIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedTransactions.length > 0 ? (
                sortedTransactions
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((txn, index) => (
                    <TableRow
                      key={`${txn.terminalID ?? ""}-${txn.acquirer?.transactionID ?? ""}-${txn.rate?.requestTime ?? ""}-${index}`}
                      sx={{
                        "&:nth-of-type(even)": { bgcolor: "blokko.taglineMist" },
                        "&:hover": { bgcolor: "blokko.tableRowHover" },
                      }}
                    >
                      <TableCell>{txn.terminalID || "N/A"}</TableCell>
                      <TableCell>
                        {txn.acquirer?.transactionID || "N/A"}
                      </TableCell>
                      <TableCell>{formatDateTime(txn?.createdAt)}</TableCell>
                      <TableCell align="right">
                        ${parseFloat(txn.transactionAmount || 0).toFixed(2)}
                      </TableCell>
                      <TableCell>
                        {txn.crypto?.currencySymbol || "N/A"}
                      </TableCell>
                      <TableCell>
                        {txn.crypto?.exchangeName ||
                          txn.crypto?.exchangeID ||
                          "N/A"}
                      </TableCell>
                      <TableCell>{formatDateTime(txn?.updatedAt)}</TableCell>
                      <TableCell>
                        {txn.blokko?.transactionStatus || "N/A"}
                      </TableCell>
                    </TableRow>
                  ))
              ) : (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    No transactions available
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={sortedTransactions.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TableContainer>
      </CardContent>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => handleFilterClose(statusFilter)}
      >
        <MenuItem onClick={() => handleFilterClose("All")}>All</MenuItem>
        <MenuItem onClick={() => handleFilterClose("E")}>Executed</MenuItem>
        <MenuItem onClick={() => handleFilterClose("P")}>Pending</MenuItem>
      </Menu>
    </Card>
  );
}

export default TransactionSearchReportTransactionsTable;
